import { Section, PageHero, EyebrowLabel } from "@/components/marketing/MarketingUI";
import useSEO from "@/lib/useSEO";

const SECTIONS = [
  {
    heading: "1. Overview",
    body: (
      <>
        <p>
          This Privacy Policy explains how Shanvi Resources Pvt. Ltd. (“SRES”, “we”, “us”)
          collects, uses, discloses and safeguards information when you access our website,
          request a demonstration, submit a job application, or use our Shanvi Resource
          Estimation Studio platform (the “Service”).
        </p>
        <p>
          By using the Service you agree to the collection and use of information in
          accordance with this policy. We only collect information required to operate the
          Service, contact you, and improve the platform.
        </p>
      </>
    ),
  },
  {
    heading: "2. Information we collect",
    body: (
      <>
        <p>We collect the following categories of information:</p>
        <ul>
          <li><b>Account information</b> — name, work email, company, role and phone number provided during signup or via the contact form.</li>
          <li><b>Uploaded data</b> — drillhole CSV files, block models, project metadata and reports that you deliberately upload to your workspace.</li>
          <li><b>Usage data</b> — pages visited, actions taken, timestamps, IP address, browser and device details.</li>
          <li><b>Support communications</b> — messages you send via the contact page, email, phone or WhatsApp.</li>
        </ul>
        <p>We do not sell personal information. We do not run behavioural advertising trackers.</p>
      </>
    ),
  },
  {
    heading: "3. How we use information",
    body: (
      <>
        <p>Information is used to:</p>
        <ul>
          <li>Deliver the Service — authenticate you, host your project data and generate reports.</li>
          <li>Respond to enquiries received via the contact form, email, phone or WhatsApp.</li>
          <li>Improve product quality — diagnose defects, measure feature adoption and prioritise the roadmap.</li>
          <li>Comply with regulatory, tax and law-enforcement obligations where required.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "4. How we share information",
    body: (
      <>
        <p>We share information only in the following limited scenarios:</p>
        <ul>
          <li><b>Service providers</b> — cloud hosting, transactional email (Resend) and analytics vendors under signed data-processing addendums.</li>
          <li><b>Legal requirements</b> — where required by law, regulation, legal process or governmental request.</li>
          <li><b>With your consent</b> — for example, when generating a public share link for a Competent Person Report.</li>
        </ul>
      </>
    ),
  },
  {
    heading: "5. Data residency & retention",
    body: (
      <>
        <p>
          Uploaded project data is stored in region-scoped MongoDB clusters. Enterprise
          tenants may request specific residency (India, Europe, Australia) as part of the
          onboarding contract.
        </p>
        <p>
          Contact submissions and job applications are retained for up to 24 months so we
          can maintain a proper audit trail of correspondence and improve support quality.
          Marketing prospects can request deletion at any time via
          <a href="mailto:info@sresources.co" className="text-brand-blue hover:underline"> info@sresources.co</a>.
        </p>
      </>
    ),
  },
  {
    heading: "6. Security",
    body: (
      <>
        <p>
          We follow industry-standard practices to protect your information — encrypted
          transport (TLS 1.2+), password hashing with modern KDFs, role-based access
          controls, audit logs and least-privilege internal access. Despite our efforts,
          no method of electronic transmission or storage is 100% secure, so we cannot
          guarantee absolute security.
        </p>
      </>
    ),
  },
  {
    heading: "7. Your rights",
    body: (
      <>
        <p>Subject to applicable law you have the right to:</p>
        <ul>
          <li>Access the personal information we hold about you.</li>
          <li>Request correction or deletion of that information.</li>
          <li>Object to or restrict certain processing activities.</li>
          <li>Export your uploaded project data.</li>
        </ul>
        <p>To exercise these rights, contact us at <a href="mailto:info@sresources.co" className="text-brand-blue hover:underline">info@sresources.co</a>.</p>
      </>
    ),
  },
  {
    heading: "8. International transfers",
    body: (
      <>
        <p>
          If you access the Service from outside India, be aware that your information may
          be transferred to, stored, and processed in India where our servers, offices and
          central operations reside. We rely on standard contractual clauses and
          equivalent safeguards where required by law.
        </p>
      </>
    ),
  },
  {
    heading: "9. Cookies",
    body: (
      <>
        <p>
          The public marketing site uses only essential cookies required for basic
          functionality. The workspace uses authentication cookies to keep you signed in.
          We do not deploy third-party advertising cookies.
        </p>
      </>
    ),
  },
  {
    heading: "10. Children",
    body: (
      <>
        <p>
          The Service is intended for professional use only. We do not knowingly collect
          personal information from anyone under 18 years old.
        </p>
      </>
    ),
  },
  {
    heading: "11. Changes to this policy",
    body: (
      <>
        <p>
          We may update this Privacy Policy from time to time. Material changes will be
          announced via email or an in-product banner at least 30 days before they take
          effect. The “Last updated” timestamp at the top of this page reflects the most
          recent revision.
        </p>
      </>
    ),
  },
  {
    heading: "12. Contact us",
    body: (
      <>
        <p>Questions about this policy can be sent to:</p>
        <ul>
          <li>Email: <a href="mailto:info@sresources.co" className="text-brand-blue hover:underline">info@sresources.co</a></li>
          <li>Phone: <a href="tel:+918079989999" className="text-brand-blue hover:underline">+91 80799 89999</a></li>
          <li>WhatsApp: <a href="https://wa.me/919938872207" target="_blank" rel="noreferrer" className="text-brand-blue hover:underline">+91 99388 72207</a></li>
        </ul>
      </>
    ),
  },
];

export default function PrivacyPolicyPage() {
  useSEO({
    title: "Privacy Policy",
    description: "How Shanvi Resources handles the data you provide to SRES — plain English, no dark patterns.",
    keywords: "SRES privacy policy, data handling",
    path: "/privacy",
  });
  return (
    <div data-testid="privacy-page">
      <PageHero
        eyebrow="Privacy Policy"
        title="How we handle your data — in plain English."
        description={`Last updated: ${new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}. This policy applies to Shanvi Resource Estimation Studio (SRES) and the sresources.co website.`}
        testid="privacy-hero"
      />

      <Section className="py-20 md:py-24" testid="privacy-body">
        <div className="grid grid-cols-1 lg:grid-cols-[240px,1fr] gap-10">
          {/* Table of contents */}
          <aside className="lg:sticky lg:top-28 self-start">
            <EyebrowLabel className="mb-4">On this page</EyebrowLabel>
            <ul className="space-y-2 text-[13px]">
              {SECTIONS.map((s, i) => (
                <li key={s.heading}>
                  <a
                    href={`#section-${i}`}
                    className="text-slate-600 hover:text-brand-blue transition-colors block leading-relaxed"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          {/* Body */}
          <article className="max-w-3xl">
            {SECTIONS.map((s, i) => (
              <div key={s.heading} id={`section-${i}`} className="mb-12 scroll-mt-28" data-testid={`privacy-section-${i}`}>
                <h2 className="font-heading font-bold text-slate-900 text-2xl tracking-tight">{s.heading}</h2>
                <div className="mt-3 space-y-3 text-slate-700 text-[15px] leading-relaxed prose-marketing">{s.body}</div>
              </div>
            ))}
          </article>
        </div>
      </Section>

      <style>{`
        .prose-marketing p { margin: 0; }
        .prose-marketing ul { list-style: disc; padding-left: 1.25rem; margin-top: 0.75rem; margin-bottom: 0.75rem; }
        .prose-marketing li { margin-bottom: 0.35rem; }
      `}</style>
    </div>
  );
}
